import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Clock, HelpCircle, Mail, MessageCircle, Phone, Send } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { PublicLayout } from './PublicLayout';

import { type AssuntoContato, type ContatoRequest, enviarContato } from '@/api/contato';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { CONTACT } from '@/constants/brand';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';

const ASSUNTOS: { value: AssuntoContato; label: string }[] = [
  { value: 'duvida', label: 'Dúvida geral' },
  { value: 'comercial', label: 'Comercial / planos' },
  { value: 'suporte', label: 'Suporte técnico' },
  { value: 'parceria', label: 'Proposta de parceria' },
  { value: 'outro', label: 'Outro assunto' },
];

interface FormState {
  nome: string;
  email: string;
  telefone: string;
  assunto: AssuntoContato | '';
  mensagem: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (form.nome.trim().length < 3) errors.nome = 'Informe seu nome (mínimo 3 caracteres).';
  if (!form.email.trim()) errors.email = 'E-mail é obrigatório.';
  else if (!EMAIL_RE.test(form.email)) errors.email = 'E-mail inválido.';
  if (!form.assunto) errors.assunto = 'Selecione um assunto.';
  if (form.mensagem.trim().length < 20) errors.mensagem = 'A mensagem deve ter pelo menos 20 caracteres.';
  return errors;
}

function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length > 6) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length > 2) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length > 0) return `(${digits}`;
  return '';
}

const INITIAL: FormState = { nome: '', email: '', telefone: '', assunto: '', mensagem: '' };

function ContactPage() {
  const { showError } = useNotification();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<FormErrors>({});
  const [enviado, setEnviado] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload: ContatoRequest) => enviarContato(payload),
    onSuccess: () => {
      setEnviado(true);
      setForm(INITIAL);
      setErrors({});
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    mutation.mutate({
      nome: form.nome.trim(),
      email: form.email.trim(),
      telefone: form.telefone.trim() || undefined,
      assunto: form.assunto as AssuntoContato,
      mensagem: form.mensagem.trim(),
    });
  }

  return (
    <PublicLayout>
      {/* Hero */}
      <div className="bg-background pb-10 pt-24 md:pt-32">
        <div className="container mx-auto max-w-screen-lg px-4">
          <span className="text-xs font-medium uppercase tracking-[0.15em] text-primary">Contato</span>
          <h1 className="mb-4 mt-1 text-3xl font-bold text-foreground">Fale Conosco</h1>
          <p className="max-w-[640px] text-base text-muted-foreground">
            Tem alguma dúvida, sugestão ou quer conhecer os planos? Envie sua mensagem — responderemos o mais rápido possível.
          </p>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="container mx-auto max-w-screen-lg px-4 pb-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          {/* Formulário */}
          <Card className="border border-border bg-card lg:col-span-3">
            <CardContent className="p-6 md:p-8">
              {enviado ? (
                <div className="flex flex-col items-center py-10 text-center">
                  <CheckCircle2 className="mb-4 h-12 w-12 text-primary" />
                  <h2 className="mb-2 text-xl font-semibold text-card-foreground">Mensagem enviada!</h2>
                  <p className="mb-6 text-sm text-muted-foreground">
                    Obrigado pelo contato. Nossa equipe responderá em breve.
                  </p>
                  <Button variant="outline" onClick={() => setEnviado(false)}>
                    Enviar outra mensagem
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
                  <h2 className="text-lg font-semibold text-card-foreground">Envie sua mensagem</h2>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="nome">
                      Nome completo <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="nome"
                      value={form.nome}
                      onChange={(e) => set('nome', e.target.value)}
                      placeholder="Seu nome"
                      aria-invalid={!!errors.nome}
                    />
                    {errors.nome && <span className="text-xs text-destructive">{errors.nome}</span>}
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="email">
                        E-mail <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => set('email', e.target.value)}
                        placeholder="seu@email.com"
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && <span className="text-xs text-destructive">{errors.email}</span>}
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        type="tel"
                        inputMode="tel"
                        value={form.telefone}
                        onChange={(e) => set('telefone', maskPhone(e.target.value))}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="assunto">
                      Assunto <span className="text-destructive">*</span>
                    </Label>
                    <Select value={form.assunto} onValueChange={(v) => set('assunto', v as AssuntoContato)}>
                      <SelectTrigger id="assunto" aria-invalid={!!errors.assunto}>
                        <SelectValue placeholder="Selecione um assunto" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSUNTOS.map((a) => (
                          <SelectItem key={a.value} value={a.value}>
                            {a.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.assunto && <span className="text-xs text-destructive">{errors.assunto}</span>}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="mensagem">
                      Mensagem <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      id="mensagem"
                      rows={5}
                      value={form.mensagem}
                      onChange={(e) => set('mensagem', e.target.value)}
                      placeholder="Descreva sua dúvida ou mensagem..."
                      aria-invalid={!!errors.mensagem}
                    />
                    {errors.mensagem && <span className="text-xs text-destructive">{errors.mensagem}</span>}
                  </div>

                  <Button type="submit" disabled={mutation.isPending} className="sm:self-start">
                    {mutation.isPending ? (
                      <Spinner className="mr-2 text-inherit" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    {mutation.isPending ? 'Enviando...' : 'Enviar mensagem'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Informações de contato */}
          <div className="flex flex-col gap-4 lg:col-span-2">
            <h2 className="text-lg font-semibold text-foreground">Outras formas de contato</h2>

            <ContactInfoCard icon={<Mail className="h-5 w-5" />} title="E-mail">
              <a href={`mailto:${CONTACT.email}`} className="text-sm text-primary no-underline hover:underline">
                {CONTACT.email}
              </a>
            </ContactInfoCard>

            {CONTACT.whatsapp && (
              <ContactInfoCard icon={<MessageCircle className="h-5 w-5" />} title="WhatsApp">
                <span className="text-sm text-muted-foreground">{CONTACT.whatsapp}</span>
              </ContactInfoCard>
            )}

            {CONTACT.telefone && (
              <ContactInfoCard icon={<Phone className="h-5 w-5" />} title="Telefone">
                <span className="text-sm text-muted-foreground">{CONTACT.telefone}</span>
              </ContactInfoCard>
            )}

            <ContactInfoCard icon={<Clock className="h-5 w-5" />} title="Horário de atendimento">
              <span className="text-sm text-muted-foreground">
                Segunda a sexta, das 9h às 18h (horário de Brasília).
              </span>
            </ContactInfoCard>

            <Card className="border border-border bg-muted">
              <CardContent className="flex items-start gap-3 p-5">
                <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="mb-1 text-sm font-semibold text-foreground">Antes de enviar</p>
                  <p className="mb-3 text-sm text-muted-foreground">
                    Talvez sua dúvida já esteja respondida nas Perguntas Frequentes.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/faq">Ver FAQ</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

function ContactInfoCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="border border-border bg-card">
      <CardContent className="flex items-start gap-3 p-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          {icon}
        </div>
        <div>
          <p className="mb-0.5 text-sm font-semibold text-card-foreground">{title}</p>
          {children}
        </div>
      </CardContent>
    </Card>
  );
}

export default ContactPage;
